import { Validators } from '@biteinc/common';
import { CreateUserType } from '@biteinc/enums';
import { CsvHelper } from '@biteinc/helpers';

export type UserImportRow = {
  username: string;
  firstName?: string;
  lastName?: string;
  type: CreateUserType;
  orgId: string;
  roleName: string;
  locationOrLocationGroupId: string;
  accessCode?: string;
  /** KOR alert keys, or the single token "all"; the server validates and expands */
  korSubscriptionList?: string[];
  /** lowercased; validated against KorAlertDeliveryMode by the server schema */
  korDelivery?: string;
  /** time of day for digest delivery, e.g. "8:00" or "6pm"; the server validates */
  korDigestTime?: string;
  /** whole number 0-100; blank or "Off" leaves threshold alerting off */
  korReadinessThresholdPercent?: string;
};

const RequiredHeaders = [
  'org id',
  'email address',
  'first name',
  'last name',
  'role',
  'location id or location group id',
];
const TypeHeader = 'type';
const AccessCodeHeader = 'access code';
const KorSubscriptionListHeader = 'kor subscription list';
const KorDeliveryHeader = 'kor delivery';
const KorDigestTimeHeader = 'kor digest time';
const KorReadinessThresholdHeader = 'kor readiness threshold percent';

const TruthyCellValues = new Set(['all', 'yes', 'y', 'true', '1']);
const FalsyCellValues = new Set(['', 'no', 'n', 'false', '0']);

const TypeByLabel: Record<string, CreateUserType> = {
  'bite admin portal': CreateUserType.BiteAdminPortal,
  'in-store': CreateUserType.InStore,
  'in store': CreateUserType.InStore,
};

const MongoIdRegex = /^[0-9a-f]{24}$/;

function parseRows(csvRows: string[][], errorMessages: string[]): UserImportRow[] {
  const headerRow = csvRows[0].map((header) => header.trim().toLowerCase());
  const missingHeaders = RequiredHeaders.filter((header) => !headerRow.includes(header));
  if (missingHeaders.length) {
    errorMessages.push(`The CSV file is missing required columns: ${missingHeaders.join(', ')}.`);
    return [];
  }

  const columnIndexByHeader = new Map<string, number>();
  headerRow.forEach((header, columnIndex) => {
    if (!columnIndexByHeader.has(header)) {
      columnIndexByHeader.set(header, columnIndex);
    }
  });

  const userRows: UserImportRow[] = [];
  csvRows.slice(1).forEach((csvRow, rowIndex) => {
    const rowNumber = rowIndex + 1;
    function cell(header: string): string {
      const columnIndex = columnIndexByHeader.get(header);
      return columnIndex !== undefined ? (csvRow[columnIndex] || '').trim() : '';
    }

    const username = cell('email address');
    if (!Validators.isEmail(username)) {
      errorMessages.push(`Row ${rowNumber}: "${username}" is not a valid email address.`);
    }

    const orgId = cell('org id').toLowerCase();
    if (!MongoIdRegex.test(orgId)) {
      errorMessages.push(
        `Row ${rowNumber}: Org Id must be the ID of an org, not "${cell('org id')}".`,
      );
    }

    // The role name is resolved against the row org's roles on the server.
    const roleName = cell('role');
    if (!roleName) {
      errorMessages.push(`Row ${rowNumber}: A role name is required.`);
    }

    // A location or location-group ID; the server resolves it within the row's org.
    const locationOrLocationGroupId = cell('location id or location group id').toLowerCase();
    if (!MongoIdRegex.test(locationOrLocationGroupId)) {
      errorMessages.push(
        `Row ${rowNumber}: "${cell('location id or location group id')}" is not a valid ` +
          'Location ID or Location Group ID.',
      );
    }

    const typeLabel = cell(TypeHeader);
    const type = typeLabel ? TypeByLabel[typeLabel.toLowerCase()] : CreateUserType.BiteAdminPortal;
    if (!type) {
      errorMessages.push(
        `Row ${rowNumber}: "${typeLabel}" is not a valid type; ` +
          'use "Bite Admin Portal" or "In-Store".',
      );
    }

    const accessCode = cell(AccessCodeHeader);
    if (type === CreateUserType.InStore) {
      if (!/^[0-9]{4}$/.test(accessCode)) {
        errorMessages.push(`Row ${rowNumber}: In-Store users need a 4-digit access code.`);
      }
    } else if (type && accessCode) {
      errorMessages.push(`Row ${rowNumber}: An access code can only be set for In-Store users.`);
    }

    // "All" (or Yes) subscribes to every KOR alert; a comma-separated list picks specific
    // alert keys (tokens are lowercased; the server resolves them to canonical keys);
    // blank or "No" skips the subscription.
    const korSubscriptionCell = cell(KorSubscriptionListHeader);
    let korSubscriptionList: string[] | undefined;
    if (TruthyCellValues.has(korSubscriptionCell.toLowerCase())) {
      korSubscriptionList = ['all'];
    } else if (!FalsyCellValues.has(korSubscriptionCell.toLowerCase())) {
      korSubscriptionList = korSubscriptionCell
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0);
      if (!korSubscriptionList.length) {
        korSubscriptionList = undefined;
        errorMessages.push(
          `Row ${rowNumber}: "${korSubscriptionCell}" is not a valid KOR Subscription List ` +
            'value; use "All", "No", or a comma-separated list of KOR alert keys.',
        );
      }
    }

    // Lowercased to match the server schema enum; values are validated there.
    const korDelivery = cell(KorDeliveryHeader).toLowerCase();
    const korDigestTime = cell(KorDigestTimeHeader);
    const korThresholdCell = cell(KorReadinessThresholdHeader);
    // "Off" matches how the subscriptions page displays an unset threshold.
    const korReadinessThresholdPercent =
      korThresholdCell.toLowerCase() === 'off' ? '' : korThresholdCell;

    const firstName = cell('first name');
    const lastName = cell('last name');
    userRows.push({
      username,
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      type: type || CreateUserType.BiteAdminPortal,
      orgId,
      roleName,
      locationOrLocationGroupId,
      ...(type === CreateUserType.InStore && accessCode && { accessCode }),
      ...(korSubscriptionList && { korSubscriptionList }),
      ...(korDelivery && { korDelivery }),
      ...(korDigestTime && { korDigestTime }),
      ...(korReadinessThresholdPercent && { korReadinessThresholdPercent }),
    });
  });

  if (!userRows.length) {
    errorMessages.push('The CSV file does not contain any user rows.');
  }

  return userRows;
}

export function parseUserImportCsv(fileText: string): {
  errorMessages: string[];
  userRows: UserImportRow[];
} {
  let csvRows: string[][];
  try {
    csvRows = CsvHelper.parseCsv(fileText);
  } catch (err) {
    return {
      errorMessages: [err instanceof Error ? err.message : 'The CSV file could not be parsed.'],
      userRows: [],
    };
  }

  if (!csvRows.length) {
    return { errorMessages: ['The CSV file is empty.'], userRows: [] };
  }

  const errorMessages: string[] = [];
  const userRows = parseRows(csvRows, errorMessages);
  return { errorMessages, userRows: errorMessages.length ? [] : userRows };
}
